/* global RadiantMP */
import { Component } from "react";
import detectIE from "shared/util/detect-ie";
import env from "shared/util/env";

type HLSVideoProps = {
  id?: string;
  className?: string;
  url: string;
  poster?: string;
  captions?: string[][];
  autoplay: boolean;
  isPlaying?: boolean;
  isFullscreen?: boolean;
  isExpanded?: boolean;
  onEnterFullscreen?: VoidFunction;
  onExitFullscreen?: VoidFunction;
  onPlaying?: VoidFunction;
  onPause?: VoidFunction;
  onEnd?: VoidFunction;
};

type RadiantMPExtraSettings = Partial<Parameters<RadiantMP["init"]>[0]>;

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
export default class HLSVideo extends Component<HLSVideoProps> {
  componentDidMount() {
    this._renderPlayer();
  }

  /**
   * @param {Object} nextProps
   */
  shouldComponentUpdate(nextProps: HLSVideoProps) {
    return (
      this.props.url !== nextProps.url ||
      this.props.poster !== nextProps.poster ||
      this.props.isPlaying !== nextProps.isPlaying ||
      this.props.isFullscreen !== nextProps.isFullscreen ||
      this.props.autoplay !== nextProps.autoplay
    );
  }

  componentWillUnmount() {
    if (this._player) {
      this._player.destroy();
    }

    this._detachOnPlayingListener?.();
    this._detachOnPauseListener?.();
    this._detachOnEndListener?.();
    this._detachOnEnterFullscreenListener?.();
    this._detachOnExitFullscreenListener?.();

    const rmpContainer = document.getElementById(this.getPlayerIdentifier());

    if (rmpContainer && rmpContainer.remove) {
      rmpContainer.remove();
    }
  }

  /**
   * @return {ReactElement}
   */
  render() {
    return <div id={this.getPlayerIdentifier()} className={this.props.className} />;
  }

  _renderPlayer() {
    if (this._player) {
      this._player.destroy();
    }

    let url = this.props.url;

    if (window.location.host === "ideas2.classdojo.com") {
      url = url.replace("ideas-video", "ideas-video2");
    }

    const extraSettings: RadiantMPExtraSettings = {};
    const { captions, poster, autoplay } = this.props;

    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

    if (captions && captions.length > 0 && !isIOS) {
      extraSettings.ccFiles = captions;
    }

    const rmp = new RadiantMP(this.getPlayerIdentifier());
    const rmpContainer = document.getElementById(this.getPlayerIdentifier());

    rmpContainer?.addEventListener("ready", () => {
      rmp.setVolume(0.5);
    });

    const { onPlaying, onPause, onEnd, onEnterFullscreen, onExitFullscreen } = this.props;

    this._detachOnPlayingListener = this.attachPlayerEvent(rmpContainer, "play", onPlaying);
    this._detachOnPauseListener = this.attachPlayerEvent(rmpContainer, "pause", onPause);
    this._detachOnEndListener = this.attachPlayerEvent(rmpContainer, "ended", onEnd);
    this._detachOnEnterFullscreenListener = this.attachPlayerEvent(rmpContainer, "enterfullscreen", onEnterFullscreen);
    this._detachOnExitFullscreenListener = this.attachPlayerEvent(rmpContainer, "exitfullscreen", onExitFullscreen);

    rmp.init({
      licenseKey: env.isDev
        ? "Kl8leWM9MDB6ZXFlMjd2MDc/cm9tNWRhc2lzMzBkYjBBJV8q"
        : "Kl8leWM9MDB6ZXFlMnllaT9yb201ZGFzaXMzMGRiMEElXyo=",
      bitrates: {
        hls: url,
      },
      delayToFade: 3000,
      width: this.props.isExpanded ? (window.innerWidth * 95) / 100 : 950,
      height: this.props.isExpanded ? (window.innerHeight * 80) / 100 : 534,
      skin: "s2",
      skinTintColor: "3E425D",
      poster,
      autoplay,
      preload: "metadata",
      hideSeekBar: true,
      hideFullscreen: false,
      hideCentralPlayButton: false,
      debug: window.location.host !== "ideas.classdojo.com",
      crossorigin: "use-credentials",
      pathToRmpFiles: "https://static.classdojo.com/radiant-media-player/",
      flashFirst: !!detectIE(),
      flashHlsStartFromLevel: 0.5,
      hlsJSStartLevel: -1,
      ...extraSettings,
    });

    this._player = rmp;
    window.rmp = rmp;
  }

  _detachOnEndListener?: VoidFunction;
  _detachOnEnterFullscreenListener?: VoidFunction;
  _detachOnExitFullscreenListener?: VoidFunction;
  _detachOnPauseListener?: VoidFunction;
  _detachOnPlayingListener?: VoidFunction;
  _player: RadiantMP | null;

  /**
   * @param {Object} nextProps
   */
  UNSAFE_componentWillReceiveProps(nextProps: HLSVideoProps) {
    if (!this._player) {
      return;
    }

    if (nextProps.isPlaying) {
      this._player.play();
    } else {
      this._player.pause();
    }
  }

  /**
   * @param {Number} to
   */
  seek(to: number) {
    if (this._player) {
      this._player.seekTo(to);
    }
  }

  getCurrentTime(): number {
    return this._player ? this._player.getCurrentTime() : -1;
  }

  getDuration(): number {
    return this._player ? this._player.getDuration() : -1;
  }

  /**
   * @param  {DOMElement} container
   * @param  {String}     event
   * @param  {Function}   callback
   * @return {Function}
   */
  attachPlayerEvent = (container: HTMLElement | null, event: string, callback?: VoidFunction) => {
    const listener = () => {
      if (callback) {
        callback();
      }
    };

    container?.addEventListener(event, listener);

    return () => container?.removeEventListener(event, listener);
  };

  /**
   * @return {String}
   */
  getPlayerIdentifier = () => (this.props.id ? `rmp-${this.props.id}` : "rmp");
}
